import {
  buildAppointmentTimeTagPresentation,
  type AppointmentTimeTagInput,
} from "../../../../../../../../../../../../../_shared/_logic/appointment-time-tags"

const objectValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const stringValue = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const formatDateTime = (value: unknown) => {
  const text = stringValue(value)

  if (!text) return ""

  const parsed = new Date(text)

  if (Number.isNaN(parsed.getTime())) return text

  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
}

const formatTimeRange = (start: unknown, end: unknown) => {
  const startText = formatDateTime(start)

  if (!startText) return ""

  const parsedEnd = new Date(stringValue(end))

  if (Number.isNaN(parsedEnd.getTime())) return startText

  return `${startText} - ${parsedEnd.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
}

export const inboxAppointmentTimeFromPayload = (
  payload: Record<string, unknown>,
): AppointmentTimeTagInput | null => {
  const slot = objectValue(payload.slot)
  const appointment = objectValue(payload.appointment)
  const appointmentRequest = objectValue(payload.appointment_request)
  const scheduleEvent = objectValue(payload.schedule_event)
  const requestedAppointment = objectValue(payload.requested_appointment)
  const providerEvent = objectValue(payload.provider_event)
  const workOrder = objectValue(payload.work_order)
  const createWorkOrder = objectValue(payload.create_work_order)
  const result: AppointmentTimeTagInput = {
    start_at:
      stringValue(
        payload.schedule_start_at,
        payload.slot_start_at,
        payload.slot_start,
        appointmentRequest.slot_start_at,
        appointmentRequest.start_at,
        payload.start_at,
        appointment.start_at,
        appointment.start,
        appointment.schedule_start_at,
        scheduleEvent.start_at,
        scheduleEvent.start,
        requestedAppointment.start_at,
        requestedAppointment.requested_start_at,
        requestedAppointment.schedule_start_at,
        providerEvent.start_at,
        slot.start_at,
        slot.start,
      ) || null,
    end_at:
      stringValue(
        payload.schedule_end_at,
        payload.slot_end_at,
        payload.slot_end,
        appointmentRequest.slot_end_at,
        appointmentRequest.end_at,
        payload.end_at,
        appointment.end_at,
        appointment.end,
        appointment.schedule_end_at,
        scheduleEvent.end_at,
        scheduleEvent.end,
        requestedAppointment.end_at,
        requestedAppointment.requested_end_at,
        requestedAppointment.schedule_end_at,
        providerEvent.end_at,
        slot.end_at,
        slot.end,
      ) || null,
    flex_type:
      stringValue(
        payload.flex_type,
        appointmentRequest.flex_type,
        appointment.flex_type,
        scheduleEvent.flex_type,
        requestedAppointment.flex_type,
        slot.flex_type,
      ) || null,
    timeframe_start_at:
      stringValue(
        payload.timeframe_start_at,
        appointmentRequest.timeframe_start_at,
        appointment.timeframe_start_at,
        scheduleEvent.timeframe_start_at,
        requestedAppointment.timeframe_start_at,
        slot.timeframe_start_at,
      ) || null,
    timeframe_end_at:
      stringValue(
        payload.timeframe_end_at,
        appointmentRequest.timeframe_end_at,
        appointment.timeframe_end_at,
        scheduleEvent.timeframe_end_at,
        requestedAppointment.timeframe_end_at,
        slot.timeframe_end_at,
      ) || null,
    customer_window_start_at:
      stringValue(
        payload.customer_window_start_at,
        appointmentRequest.customer_window_start_at,
        appointment.customer_window_start_at,
        scheduleEvent.customer_window_start_at,
        requestedAppointment.customer_window_start_at,
        slot.customer_window_start_at,
      ) || null,
    customer_window_end_at:
      stringValue(
        payload.customer_window_end_at,
        appointmentRequest.customer_window_end_at,
        appointment.customer_window_end_at,
        scheduleEvent.customer_window_end_at,
        requestedAppointment.customer_window_end_at,
        slot.customer_window_end_at,
      ) || null,
    estimated_duration_min:
      stringValue(
        payload.estimated_duration_min,
        appointmentRequest.estimated_duration_min,
        appointment.estimated_duration_min,
        scheduleEvent.estimated_duration_min,
        requestedAppointment.estimated_duration_min,
        workOrder.estimated_duration_min,
        createWorkOrder.estimated_duration_min,
      ) || null,
    duration_minutes:
      stringValue(
        payload.duration_minutes,
        appointmentRequest.duration_minutes,
        appointment.duration_minutes,
        scheduleEvent.duration_minutes,
        requestedAppointment.duration_minutes,
      ) || null,
  }

  return result.start_at ||
    result.end_at ||
    result.timeframe_start_at ||
    result.timeframe_end_at ||
    result.customer_window_start_at ||
    result.customer_window_end_at
    ? result
    : null
}

export const inboxScheduleLabelFromPayload = (payload: Record<string, unknown>) => {
  const appointmentTime = inboxAppointmentTimeFromPayload(payload)
  const appointmentPresentation = buildAppointmentTimeTagPresentation(appointmentTime)

  if (appointmentTime && appointmentPresentation.label !== "Kein Termin")
    return appointmentPresentation.label

  const slot = objectValue(payload.slot)
  const scheduleEvent = objectValue(payload.schedule_event)

  return (
    stringValue(payload.schedule_label, payload.appointment_label, scheduleEvent.label) ||
    formatTimeRange(
      stringValue(
        payload.schedule_start_at,
        payload.slot_start_at,
        payload.slot_start,
        scheduleEvent.start_at,
        slot.start_at,
      ),
      stringValue(
        payload.schedule_end_at,
        payload.slot_end_at,
        payload.slot_end,
        scheduleEvent.end_at,
        slot.end_at,
      ),
    )
  )
}
